import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Header, Filter } from './types';
import { useFilterBuilder } from './useFilterBuilder';
import FieldsDropdown from './FieldsDropdown';
import FiltersDropdown from './FiltersDropdown';
import * as S from './FilterMenu.style';
import CurrencyInput from '../CurrencyInput';

interface FilterMenuProps {
  headers: Header[];
  onApplyFilter: (filter: Filter) => void;
  onClose: () => void;
  filters?: Filter[];
  anchorRef: React.MutableRefObject<HTMLDivElement | null>;
}

const FilterMenu: React.FC<FilterMenuProps> = ({ headers, onApplyFilter, onClose, filters = [], anchorRef }) => {
  const {
    selectedField,
    selectedFilterType,
    filterValue,
    displayValue,
    setFilterValue,
    setDisplayValue,
    handleSelectField,
    handleSelectFilterType,
    handleApply: baseHandleApply
  } = useFilterBuilder({ onApplyFilter });
  const [intervalValue, setIntervalValue] = useState({ start: '', end: '' });

  const handleApply = () => {
    if (!selectedField || !selectedFilterType) return;
    if (selectedField.type === 'date' && selectedFilterType === 'Intervalo') {
      if (!intervalValue.start || !intervalValue.end) return;
      const newFilter: Filter = {
        key: selectedField.key,
        label: selectedField.label,
        filterType: selectedFilterType,
        value: { start: intervalValue.start, end: intervalValue.end },
        displayValue: `${intervalValue.start} até ${intervalValue.end}`,
        fieldType: selectedField.type,
      };
      onApplyFilter(newFilter);
      setIntervalValue({ start: '', end: '' });
      return;
    }
    baseHandleApply();
  };

  const [isFieldsDropdownOpen, setFieldsDropdownOpen] = useState(false);
  const [isFiltersDropdownOpen, setFiltersDropdownOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;

    if (!anchor) {
      setMenuPosition(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 10,
      left: rect.left + rect.width / 2,
    });
  }, [anchorRef]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const onApplyAndClose = () => {
    handleApply();
    onClose();
  }

  const availableHeaders = headers.filter(
    h => !filters.some(f => f.key === h.key)
  );

  if (!menuPosition) {
    return null;
  }

  const menuContent = (
    <S.MenuContainer ref={menuRef} style={{ top: menuPosition.top, left: menuPosition.left }}>
      <h4>Construir Filtro</h4>
      <S.BuilderRow>
        <S.FilterSelectWrapper>
          <S.FilterInput
            readOnly
            value={selectedField?.label || ''}
            placeholder="Selecione um campo"
            onClick={() => setFieldsDropdownOpen(true)}
          />
          {isFieldsDropdownOpen && (
            <FieldsDropdown
              headers={availableHeaders}
              onSelect={(field) => {
                handleSelectField(field);
                setFieldsDropdownOpen(false);
              }}
              close={() => setFieldsDropdownOpen(false)}
            />
          )}
        </S.FilterSelectWrapper>
      </S.BuilderRow>

      {selectedField && selectedField.key !== 'companyTypes' && (
        <S.BuilderRow>
          <S.FilterSelectWrapper>
            <S.FilterInput
              readOnly
              value={selectedFilterType}
              placeholder="Selecione a condição"
              onClick={() => setFiltersDropdownOpen(true)}
            />
            {isFiltersDropdownOpen && (
              <FiltersDropdown
                fieldType={selectedField.type}
                onSelect={(type) => {
                  handleSelectFilterType(type);
                  setFiltersDropdownOpen(false);
                }}
                close={() => setFiltersDropdownOpen(false)}
              />
            )}
          </S.FilterSelectWrapper>
        </S.BuilderRow>
      )}

      {selectedField && selectedField.type !== 'boolean' && (
        selectedField.selectOptions ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <select
                value={filterValue}
                onChange={e => {
                  setFilterValue(e.target.value);
                  setDisplayValue(e.target.value);
                  handleSelectFilterType('Igual a');
                }}
                style={{
                  background: 'transparent',
                  border: 0,
                  width: '100%',
                  height: '100%',
                  padding: '0 0.5rem',
                  fontSize: '0.9rem',
                  outline: 0,
                  cursor: 'pointer'
                }}
              >
                <option value="">Selecione</option>
                {selectedField.selectOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : selectedField.type === 'date' && selectedFilterType === 'Intervalo' ? (
          <>
            <S.BuilderRow>
              <S.FilterSelectWrapper>
                <S.FilterInput
                  type="date"
                  placeholder="Data inicial"
                  value={intervalValue.start}
                  onChange={e => setIntervalValue(v => ({ ...v, start: e.target.value }))}
                />
              </S.FilterSelectWrapper>
            </S.BuilderRow>
            <S.BuilderRow>
              <S.FilterSelectWrapper>
                <S.FilterInput
                  type="date"
                  placeholder="Data final"
                  value={intervalValue.end}
                  onChange={e => setIntervalValue(v => ({ ...v, end: e.target.value }))}
                />
              </S.FilterSelectWrapper>
            </S.BuilderRow>
          </>
        ) : selectedField.type === 'date' ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                type="date"
                placeholder="Selecione a data"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : selectedField.type === 'currency' ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <CurrencyInput
                id="filter-currency"
                name="filter-currency"
                value={filterValue !== '' ? parseFloat(filterValue) : NaN}
                onValueChange={(formatted, __, values) => {
                  if (values?.float === undefined) {
                    setFilterValue('');
                    setDisplayValue('');
                    return;
                  }
                  setFilterValue(values.float.toString());
                  setDisplayValue(values.formatted ?? formatted ?? '');
                }}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                type={selectedField.type === 'number' ? 'number' : 'text'}
                placeholder="Digite aqui"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
                inputMode={selectedField.type === 'number' ? 'decimal' : undefined}
                step={selectedField.type === 'number' ? 'any' : undefined}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        )
      )}

      <S.ApplyButton onClick={onApplyAndClose}>Aplicar Filtro</S.ApplyButton>
    </S.MenuContainer>
  );

  return createPortal(menuContent, document.body);
};

export default FilterMenu;